import { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';

type TableSortDir = 'asc' | 'desc';

interface SortableColumn<TKey extends string> {
  key: TKey;
  sortable?: boolean;
}

interface UseTableViewStateConfig<TFilters, TSortKey extends string> {
  emptyFilters: TFilters;
  initialFilters?: TFilters;
  initialSortKey: TSortKey | null;
  initialSortDir?: TableSortDir;
  debounceMs?: number;
  getSortDirForColumn?: (column: SortableColumn<TSortKey>) => TableSortDir;
}

export function useTableViewState<TFilters, TSortKey extends string>({
  emptyFilters,
  initialFilters,
  initialSortKey,
  initialSortDir = 'desc',
  debounceMs = 300,
  getSortDirForColumn,
}: UseTableViewStateConfig<TFilters, TSortKey>) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TFilters>(initialFilters ?? emptyFilters);
  const [sortKey, setSortKey] = useState<TSortKey | null>(initialSortKey);
  const [sortDir, setSortDir] = useState<TableSortDir>(initialSortDir);
  const debouncedFilters = useDebounce(filters, debounceMs);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, sortKey, sortDir]);

  function setFilter<K extends keyof TFilters>(key: K, value: TFilters[K]) {
    setFilters((previous) => ({ ...previous, [key]: value }));
  }

  function updateFilters(patch: Partial<TFilters>) {
    setFilters((previous) => ({ ...previous, ...patch }));
  }

  function resetFilters() {
    setFilters(emptyFilters);
    setPage(1);
  }

  function handleSort(column: SortableColumn<TSortKey>) {
    if (!column.sortable) return;
    if (sortKey === column.key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(column.key);
    setSortDir(getSortDirForColumn?.(column) ?? initialSortDir);
  }

  return {
    page,
    setPage,
    filters,
    setFilter,
    updateFilters,
    resetFilters,
    debouncedFilters,
    sortKey,
    setSortKey,
    sortDir,
    setSortDir,
    handleSort,
  };
}
